/**
 * TanStack Router integration for page-view tracking.
 *
 * Call `attachRouterPageViewTracking(router)` once in your app's entry
 * after `createRouter()`. It subscribes to router resolution events and
 * fires `track("page_viewed", { path, search, from })` on every navigation.
 *
 * Framework-agnostic: we use a structural type for the router instead of
 * importing from `@tanstack/react-router` directly, so this file compiles
 * and tree-shakes cleanly for any consumer.
 *
 * @example
 * ```ts
 * import { createRouter } from "@tanstack/react-router";
 * import { attachRouterPageViewTracking } from "@engagexp/analytics";
 *
 * const router = createRouter({ routeTree, defaultPreload: "intent" });
 * attachRouterPageViewTracking(router);
 * ```
 */
import { track } from "../tracker";

/**
 * Minimal structural shape of a TanStack Router instance.
 * Compatible with `createRouter()` from `@tanstack/react-router`.
 */
export interface RouterLike {
  subscribe(
    eventType: "onResolved",
    handler: (event: {
      toLocation?: { pathname?: string; searchStr?: string };
      fromLocation?: { pathname?: string };
    }) => void,
  ): () => void;
}

export interface PageViewOptions {
  /**
   * Called to decide whether to track a given navigation.
   * Default: always true.
   */
  shouldTrack?: (pathname: string) => boolean;
  /**
   * Extra static properties merged into every page_viewed event.
   */
  extraProperties?: Record<string, string | number | boolean>;
}

/**
 * Subscribe to router navigation and fire `page_viewed` events.
 *
 * Returns an unsubscribe function that detaches the listener.
 */
export function attachRouterPageViewTracking(
  router: RouterLike,
  options: PageViewOptions = {},
): () => void {
  const { shouldTrack, extraProperties } = options;

  return router.subscribe("onResolved", (event) => {
    const pathname = event.toLocation?.pathname;
    if (!pathname) return;
    if (shouldTrack && !shouldTrack(pathname)) return;

    const searchStr = event.toLocation?.searchStr ?? "";
    const fromPath = event.fromLocation?.pathname ?? null;

    track("page_viewed", {
      path: pathname,
      search: searchStr || null,
      from: fromPath,
      ...(extraProperties ?? {}),
    });
  });
}
